import { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Paper,
  InputAdornment,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";

import Grid from "@mui/material/Grid";
import "../../common/Common.css";
import { DataGrid } from "@mui/x-data-grid";

import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";

import ReusableSnackbar from "../../shared/Alert/ReusableSnackbar";
import ConfirmDialog from "../../shared/ConfirmDialog/ConfirmDialog";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import api from "../../services/api";


// ---------------------------------------------------------
// Palette
// ---------------------------------------------------------

const palette = {
  indigo: "#4B49AC",
  indigoDark: "#37357D",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
  teal: "#12A594",
  tealSoft: "#DDF6F1",
  coral: "#FF6B57",
  coralSoft: "#FFEFEC",
};


// ---------------------------------------------------------
// Action button
// ---------------------------------------------------------

const actionIconBtn = (color, hoverBg) => ({
  width: 30,
  height: 30,
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color,
  transition: "background-color 120ms ease",

  "&:hover": {
    backgroundColor: hoverBg,
  },
});


// ---------------------------------------------------------
// Component
// ---------------------------------------------------------

export default function InvoiceTable(props) {
  const {
    rows = [],
    getInvoiceDetails,
  } = props;

  const [searchQuery, setSearchQuery] = useState("");

  const [selectedDate, setSelectedDate] =
    useState(null);

  const [paymentMode, setPaymentMode] =
    useState("");

  const [selectedInvoiceId, setSelectedInvoiceId] =
    useState(null);

  const [selectedPatientId, setSelectedPatientId] =
    useState(null);

  const [open, setOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] =
    useState({
      status: false,
      message: "",
      severity: "success",
    });

  const APP_URL = process.env.REACT_APP_URL;


  // -------------------------------------------------------
  // Helpers
  // -------------------------------------------------------

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";

    const date = new Date(dateStr);

    if (isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatBillingPeriod = (fromDate, toDate) => {
    if (!fromDate || !toDate) return "-";
  
    const format = (date) =>
      new Date(date)
        .toLocaleDateString("en-US", {
          day: "2-digit",
          month: "short",
        })
        .toUpperCase();
  
    return `${format(fromDate)} - ${format(toDate)}`;
  };


  const currency = (value) => {
    const amount = Number(value || 0);

    return `₹${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };


  const getMonthName = (month) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return months[(month || 1) - 1];
  };


  // -------------------------------------------------------
  // Payment mode
  // -------------------------------------------------------

  const handleChange = async (
    event,
    invoiceId
  ) => {
    const mode = event.target.value;

    setPaymentMode(mode);

    try {
      const response = await api.patch(
        `app/invoice/${invoiceId}/payment/`,
        {
          payment_mode: mode,
        }
      );

      if (response?.status === 200) {
        setSnackbarOpen({
          status: true,
          message:
            "Payment Mode Updated Successfully!",
          severity: "success",
        });

        getInvoiceDetails?.();
      }
    } catch (error) {
      console.error(
        "Failed to update payment mode:",
        error
      );

      setSnackbarOpen({
        status: true,
        message:
          error?.response?.data?.detail ||
          "Failed to update payment mode.",
        severity: "error",
      });
    }
  };


  // -------------------------------------------------------
  // Preview
  // -------------------------------------------------------

  const handlePreview = (invoiceId, branchId) => {
    const url =
      APP_URL +
      `/invoice/preview?invoice_id=${invoiceId}&branch_id=${branchId}&view=patient`;
  
    window.open(url, "_blank");
  };


  // -------------------------------------------------------
  // Send email
  // -------------------------------------------------------

  const handleSendMail = (
    invoiceId,
    patientId
  ) => {
    setSelectedInvoiceId(invoiceId);
    setSelectedPatientId(patientId);
    setOpen(true);
  };


  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedInvoiceId(null);
    setSelectedPatientId(null);
  };


  const handleAgreeSendMail = async () => {
    try {
      await api.post(
        `app/invoice/${selectedInvoiceId}/send-email/`,
        {
          patient_id: selectedPatientId,
        }
      );

      setSnackbarOpen({
        status: true,
        message:
          "Invoice email sent successfully!",
        severity: "success",
      });

      handleCloseDialog();

      getInvoiceDetails?.();
    } catch (error) {
      console.error(
        "Failed to send invoice email:",
        error
      );

      setSnackbarOpen({
        status: true,
        message:
          error?.response?.data?.detail ||
          "Failed to send invoice email.",
        severity: "error",
      });
    }
  };


  const handleSnackClose = () => {
    setSnackbarOpen({
      status: false,
      message: "",
      severity: "",
    });
  };


  // -------------------------------------------------------
  // Table columns
  // -------------------------------------------------------

  const columns = [
    {
      field: "invoice_no",
      headerName: "Invoice No",
      minWidth: 170,
      flex: 1.2,

      renderCell: (params) => (
        <span
          style={{
            fontWeight: 600,
            color: palette.indigo,
          }}
        >
          {params.value}
        </span>
      ),
    },

    {
      field: "patient_name",
      headerName: "Patient",
      minWidth: 160,
      flex: 1.2,

      renderCell: (params) => (
        <span
          style={{
            fontWeight: 600,
            color: palette.ink,
          }}
        >
          {params.value}
        </span>
      ),
    },

    {
      field: "period",
      headerName: "Period",
      minWidth: 105,
      flex: 0.6,
    
      renderCell: (params) => (
        <span
          style={{
            fontSize: "11.5px",
            color: palette.slate,
            whiteSpace: "nowrap",
          }}
        >
          {formatBillingPeriod(
            params.row.billing_from_date,
            params.row.billing_to_date
          )}
        </span>
      ),
    },

    {
      field: "invoice_date",
      headerName: "Invoice Date",
      minWidth: 120,
      flex: 0.9,

      renderCell: (params) => (
        <span>
          {formatDate(params.value)}
        </span>
      ),
    },

    {
      field: "total_amount",
      headerName: "Amount",
      minWidth: 110,
      flex: 0.8,

      renderCell: (params) => (
        <span
          style={{
            fontWeight: 600,
            color: palette.ink,
          }}
        >
          {currency(params.value)}
        </span>
      ),
    },

    {
      field: "payment_status",
      headerName: "Payment",
      minWidth: 110,
      flex: 0.8,

      renderCell: (params) => {
        const status =
          params.value || "PENDING";

        const isPaid =
          status === "PAID";

        return (
          <Chip
            size="small"
            label={status}
            sx={{
              fontSize: "11.5px",
              fontWeight: 700,

              color: isPaid
                ? "#0B6B5F"
                : palette.slate,

              backgroundColor: isPaid
                ? palette.tealSoft
                : "#F1F2F4",
            }}
          />
        );
      },
    },

    {
      field: "payment_mode",
      headerName: "Payment Mode",
      minWidth: 155,
      flex: 1,

      renderCell: (params) => {
        const currentMode =
          params.row.payment_mode || "";

        const isPending =
          params.row.payment_status ===
          "PENDING";

        return (
          <FormControl
            sx={{
              m: 0,
              minWidth: 120,
            }}
            size="small"
          >
            <Select
              value={
                paymentMode ||
                currentMode
              }
              onChange={(e) =>
                handleChange(
                  e,
                  params.row.id
                )
              }
              displayEmpty
              disabled={!isPending}
              sx={{
                paddingY: "2px",
                paddingX: "8px",
                fontSize: "0.875rem",
                borderRadius: "8px",

                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      palette.line,
                  },

                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      palette.indigo,
                  },
              }}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>

              <MenuItem value="CARD">
                CARD
              </MenuItem>

              <MenuItem value="CASH">
                CASH
              </MenuItem>

              <MenuItem value="UPI">
                UPI
              </MenuItem>

              <MenuItem value="BANK_TRANSFER">
                BANK TRANSFER
              </MenuItem>
            </Select>
          </FormControl>
        );
      },
    },

    {
      field: "invoice_status",
      headerName: "Status",
      minWidth: 110,
      flex: 0.8,

      renderCell: (params) => {
        const status =
          params.value || "GENERATED";

        return (
          <Chip
            size="small"
            label={status}
            sx={{
              fontSize: "11.5px",
              fontWeight: 700,

              color:
                status === "GENERATED"
                  ? "#0B6B5F"
                  : palette.slate,

              backgroundColor:
                status === "GENERATED"
                  ? palette.tealSoft
                  : "#F1F2F4",
            }}
          />
        );
      },
    },

    {
      field: "action",
      headerName: "Action",
      flex: 0.8,
      minWidth: 120,

      sortable: false,
      filterable: false,

      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent:
              "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
            gap: "6px",
          }}
        >
          {/* Preview */}
          <Tooltip title="Preview">
            <Box
              sx={actionIconBtn(
                palette.slate,
                "#F1F5F9"
              )}
              onClick={() =>
                handlePreview(
                  params.row.id,
                  params.row.branch_id
                )
              }
            >
              <RemoveRedEyeOutlinedIcon
                sx={{
                  fontSize: "17px",
                }}
              />
            </Box>
          </Tooltip>

          {/* Send Email */}
          <Tooltip title="Send invoice email">
            <span>
              <IconButton
                onClick={() =>
                  handleSendMail(
                    params.row.id,
                    params.row.patient_id
                  )
                }
                sx={{
                  ...actionIconBtn(
                    palette.indigo,
                    "#F1F5F9"
                  ),
                  padding: 0,
                }}
              >
                <ForwardToInboxOutlinedIcon
                  sx={{
                    fontSize: "17px",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];


  // -------------------------------------------------------
  // Filter
  // -------------------------------------------------------

  const filteredRows =
    rows?.filter((row) => {
      const search =
        searchQuery
          .toLowerCase()
          .trim();

      const matchesSearch =
        !search ||
        Object.values(row).some(
          (value) =>
            value !== null &&
            value !== undefined &&
            value
              .toString()
              .toLowerCase()
              .includes(search)
        );

      let matchesDate = true;

      if (selectedDate) {
        matchesDate =
          row.invoice_year ===
            selectedDate.year() &&
          row.invoice_month ===
            selectedDate.month() + 1;
      }

      return (
        matchesSearch &&
        matchesDate
      );
    });


  // -------------------------------------------------------
  // Render
  // -------------------------------------------------------

  return (
    <>
      <Box
        className="tabcontent_box"
        sx={{
          backgroundColor:
            palette.bg,
        }}
      >
        {/* Filter bar */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: `1px solid ${palette.line}`,
            padding: "16px 20px",
            marginBottom: "16px",
            backgroundColor: "#fff",
          }}
        >
          <Grid
            container
            spacing={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid
              size={4}
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  color: palette.slate,
                }}
              >
                {filteredRows.length} invoice
                {filteredRows.length ===
                1
                  ? ""
                  : "s"}
              </span>
            </Grid>

            <Grid size={3.5}>
              <LocalizationProvider
                dateAdapter={
                  AdapterDayjs
                }
              >
                <DatePicker
                  views={[
                    "year",
                    "month",
                  ]}
                  label="Filter period"
                  value={selectedDate}
                  onChange={(value) =>
                    setSelectedDate(
                      value
                    )
                  }
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,

                      sx: {
                        "& .MuiOutlinedInput-root":
                          {
                            borderRadius:
                              "10px",
                            backgroundColor:
                              palette.bg,
                            fontSize:
                              "13.5px",

                            "& fieldset":
                              {
                                borderColor:
                                  palette.line,
                              },

                            "&:hover fieldset":
                              {
                                borderColor:
                                  palette.slate,
                              },

                            "&.Mui-focused fieldset":
                              {
                                borderColor:
                                  palette.indigo,
                              },
                          },
                      },
                    },
                  }}
                  slots={{
                    openPickerIcon:
                      CalendarMonthOutlinedIcon,
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid
              size={4}
              sx={{
                textAlign: "right",
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search invoices..."
                value={searchQuery}
                onChange={(e) =>
                  setSearchQuery(
                    e.target.value
                  )
                }
                sx={{
                  "& .MuiOutlinedInput-root":
                    {
                      borderRadius:
                        "10px",
                      backgroundColor:
                        palette.bg,
                      fontSize:
                        "13.5px",

                      "& fieldset": {
                        borderColor:
                          palette.line,
                      },

                      "&:hover fieldset":
                        {
                          borderColor:
                            palette.slate,
                        },

                      "&.Mui-focused fieldset":
                        {
                          borderColor:
                            palette.indigo,
                        },
                    },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon
                        sx={{
                          fontSize:
                            "18px",
                          color:
                            palette.slate,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Invoice table */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: `1px solid ${palette.line}`,
            overflow: "hidden",
          }}
        >
          <DataGrid
            sx={{
              fontFamily:
                "Nunito, sans-serif",
              border: "none",

              "& .MuiDataGrid-columnHeaders":
                {
                  backgroundColor:
                    palette.bg,
                  borderBottom: `1px solid ${palette.line}`,
                },

              "& .MuiDataGrid-columnHeaderTitle":
                {
                  fontSize: "12.5px",
                  fontWeight: 700,
                  color: palette.slate,
                  textTransform:
                    "uppercase",
                  letterSpacing:
                    "0.03em",
                },

              "& .MuiDataGrid-cell":
                {
                  fontSize: "13px",
                  borderBottom: `1px solid ${palette.line}`,
                },

              "& .MuiDataGrid-row:hover":
                {
                  backgroundColor:
                    "#F5F6F8",
                },

              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                {
                  outline: "none",
                },

              "& .MuiDataGrid-footerContainer":
                {
                  borderTop: `1px solid ${palette.line}`,
                },
            }}
            rows={filteredRows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            disableRowSelectionOnClick
            getRowId={(row) =>
              row.id
            }
          />
        </Paper>
      </Box>

      {/* Email confirmation */}
      <ConfirmDialog
        open={open}
        title="Confirm Mail Sending"
        description="Are you sure you want to send this invoice by email?"
        agreeText="Send"
        disagreeText="Cancel"
        onClose={handleCloseDialog}
        onAgree={handleAgreeSendMail}
      />

      {/* Snackbar */}
      <ReusableSnackbar
        open={snackbarOpen.status}
        message={
          snackbarOpen.message
        }
        onClose={handleSnackClose}
        severity={
          snackbarOpen.severity
        }
      />
    </>
  );
}